import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  CheckCircleIcon,
  KeyIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { formatPortalDateTime } from './portalLabels';
import { usePortalState } from './PortalState';

type OpenPanel = 'tasks' | 'notifications' | 'help' | 'account' | null;

type PortalHeaderProps = {
  onOpenMenu: () => void;
};

export function PortalHeader({ onOpenMenu }: PortalHeaderProps) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const {
    data,
    needsYouCount,
    openFeedback,
    threadsAwaitingReply,
    markAllNotificationsRead,
    markNotificationRead,
  } = usePortalState();
  const [open, setOpen] = useState<OpenPanel>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const unread = data.activity.filter((item) => !item.read);
  const firstName = data.clientName.split(' ')[0] || data.clientName;
  const initial = firstName.charAt(0).toUpperCase();
  const awaitingIds = new Set(threadsAwaitingReply.map((comment) => comment.targetId));
  const inProgress = data.backlog.filter(
    (item) => item.status === 'inProgress' && !awaitingIds.has(item.id),
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setOpen(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function toggle(panel: OpenPanel): void {
    setOpen((current) => (current === panel ? null : panel));
  }

  async function handleLogout(): Promise<void> {
    setOpen(null);
    try {
      await logout();
    } catch {
      // A pré-visualização pode não ter sessão.
    }
    navigate('/login');
  }

  return (
    <header className="relative shrink-0 bg-primary-500 text-white">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 md:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            className="shrink-0 rounded-xl p-2 text-white hover:bg-white/10 md:hidden"
            onClick={onOpenMenu}
            aria-label="Abrir menu"
          >
            <span className="text-sm font-semibold">Menu</span>
          </button>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold leading-tight text-white">Portal do cliente</p>
            <p className="truncate text-xs leading-tight text-white/80">{data.companyName}</p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1 sm:gap-2" ref={panelRef}>
          <div className="relative">
            <button
              type="button"
              className="relative rounded-xl p-2 text-white hover:bg-white/10"
              title="Tarefas"
              aria-label="Tarefas"
              aria-expanded={open === 'tasks'}
              onClick={() => toggle('tasks')}
            >
              <CheckCircleIcon className="h-6 w-6" />
              {needsYouCount > 0 ? (
                <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[10px] font-bold text-primary-900">
                  {needsYouCount > 9 ? '9+' : needsYouCount}
                </span>
              ) : null}
            </button>
            {open === 'tasks' ? (
              <div className="absolute right-0 z-30 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-secondary-200 bg-white py-2 text-secondary-800 shadow-lg">
                <h2 className="px-4 py-2 text-sm font-semibold text-secondary-900">Tarefas</h2>
                {needsYouCount === 0 && inProgress.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-secondary-600">Nenhuma tarefa pendente.</p>
                ) : (
                  <ul className="max-h-80 overflow-y-auto">
                    {openFeedback.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/portal/feedback/${item.id}`}
                          className="block px-4 py-2 hover:bg-secondary-50"
                          onClick={() => setOpen(null)}
                        >
                          <span className="block text-sm font-semibold text-secondary-900">{item.title}</span>
                          <span className="block text-xs text-secondary-500">Feedback</span>
                        </Link>
                      </li>
                    ))}
                    {threadsAwaitingReply.map((comment) => {
                      const backlog = data.backlog.find((item) => item.id === comment.targetId);
                      const roadmap = data.roadmap.find((item) => item.id === comment.targetId);
                      return (
                        <li key={comment.id}>
                          <Link
                            to={`/portal/projects/${comment.projectId}?tab=backlog&item=${comment.targetId}`}
                            className="block px-4 py-2 hover:bg-secondary-50"
                            onClick={() => setOpen(null)}
                          >
                            <span className="block text-sm font-semibold text-secondary-900">
                              {backlog?.title ?? roadmap?.title ?? 'Item do projeto'}
                            </span>
                            <span className="block text-xs text-secondary-500">Resposta para {comment.authorName}</span>
                          </Link>
                        </li>
                      );
                    })}
                    {inProgress.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/portal/projects/${item.projectId}?tab=backlog&item=${item.id}`}
                          className="block px-4 py-2 hover:bg-secondary-50"
                          onClick={() => setOpen(null)}
                        >
                          <span className="block text-sm font-semibold text-secondary-900">{item.title}</span>
                          <span className="block text-xs text-secondary-500">Em andamento</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
          </div>

          <div className="relative">
            <button
              type="button"
              className="relative rounded-xl p-2 text-white hover:bg-white/10"
              title="Notificações"
              aria-label="Notificações"
              aria-expanded={open === 'notifications'}
              onClick={() => toggle('notifications')}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
                />
              </svg>
              {unread.length > 0 ? (
                <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {unread.length > 9 ? '9+' : unread.length}
                </span>
              ) : null}
            </button>
            {open === 'notifications' ? (
              <div className="absolute right-0 z-30 mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-2xl border border-secondary-200 bg-white text-secondary-800 shadow-lg">
                <div className="flex items-center justify-between border-b border-secondary-100 px-4 py-3">
                  <h2 className="text-sm font-semibold">Notificações</h2>
                  {unread.length > 0 ? (
                    <button
                      type="button"
                      className="text-xs font-medium text-primary-600 hover:underline"
                      onClick={markAllNotificationsRead}
                    >
                      Marcar todas como lidas
                    </button>
                  ) : null}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {data.activity.length === 0 ? (
                    <p className="px-4 py-6 text-sm text-secondary-500">Nenhuma notificação.</p>
                  ) : (
                    data.activity.map((item) => (
                      <Link
                        key={item.id}
                        to={item.href}
                        className={`block border-b border-secondary-50 px-4 py-3 ${item.read ? 'bg-white' : 'bg-primary-50/40'}`}
                        onClick={() => {
                          markNotificationRead(item.id);
                          setOpen(null);
                        }}
                      >
                        <span className="block text-sm font-semibold text-secondary-900">{item.title}</span>
                        <span className="mt-1 block text-sm text-secondary-600">{item.detail}</span>
                        <span className="mt-1 block text-xs text-secondary-400">
                          {formatPortalDateTime(item.happenedAt)}
                        </span>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div className="relative">
            <button
              type="button"
              className="rounded-xl p-2 text-white hover:bg-white/10"
              title="Ajuda"
              aria-label="Ajuda"
              aria-expanded={open === 'help'}
              onClick={() => toggle('help')}
            >
              <QuestionMarkCircleIcon className="h-6 w-6" />
            </button>
            {open === 'help' ? (
              <div className="absolute right-0 z-30 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-secondary-200 bg-white py-2 text-secondary-800 shadow-lg">
                <h2 className="px-4 py-2 text-sm font-semibold text-secondary-900">Documentação</h2>
                {data.documentation.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-secondary-600">Nenhum documento publicado.</p>
                ) : (
                  <ul>
                    {data.documentation.map((page) => (
                      <li key={page.id}>
                        <Link
                          to={`/portal/documentation/${page.id}`}
                          className="block px-4 py-2 text-sm text-primary-700 hover:bg-secondary-50"
                          onClick={() => setOpen(null)}
                        >
                          {page.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
          </div>

          <div className="relative">
            <button
              type="button"
              className="flex items-center gap-2 rounded-xl px-2 py-1 text-white hover:bg-white/10 sm:gap-3"
              aria-label="Conta"
              aria-expanded={open === 'account'}
              onClick={() => toggle('account')}
            >
              <span className="hidden whitespace-nowrap text-sm text-white/80 sm:inline">
                Olá, <span className="font-semibold text-white">{firstName}</span>
              </span>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-sm font-semibold text-primary-500">
                {initial}
              </span>
            </button>
            {open === 'account' ? (
              <div className="absolute right-0 z-30 mt-2 w-56 rounded-2xl border border-secondary-200 bg-white py-2 text-secondary-800 shadow-lg">
                <Link
                  to="/portal/account/profile"
                  className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-secondary-50"
                  onClick={() => setOpen(null)}
                >
                  <UserCircleIcon className="h-5 w-5 text-primary-600" />
                  Atualizar perfil
                </Link>
                <Link
                  to="/portal/account/password"
                  className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-secondary-50"
                  onClick={() => setOpen(null)}
                >
                  <KeyIcon className="h-5 w-5 text-primary-600" />
                  Alterar senha
                </Link>
                <hr className="my-2 border-secondary-100" />
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                  onClick={() => void handleLogout()}
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                  Sair
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}
